import type { metaEn } from './meta-en.ts';

type DeepString<T> = T extends string ? string : { [K in keyof T]: DeepString<T[K]> };

/** Italian strings of what playing earns. Typed against the English shape. */
export const metaIt: DeepString<typeof metaEn> = {
  meta: {
    gate: {
      signIn: 'Accedi per vedere i tuoi progressi',
      signInBody:
        'XP, fedina penale, trofei e classifiche appartengono a un account (basta anche un account ospite). I colpi contro i bot del Solista non contano.',
      signInAction: 'Apri l’account',
      unavailable: 'I progressi non sono disponibili in questa versione',
      unavailableBody: 'Questa build non ha un servizio account, quindi non c’è dove conservarli.',
      loading: 'Caricamento…',
      failed: 'Non sono riuscito a caricare i progressi. Riprova fra un momento.',
      retry: 'Riprova',
    },
    progress: {
      title: 'Fedina penale',
      level: 'Livello {{level}}',
      xp: '{{xp}} XP',
      toNext: '{{needed}} XP al livello {{next}}',
      levelBar: 'Avanzamento verso il livello successivo',
      stats: 'Il curriculum',
      season: 'Stagione {{n}}',
      ratings: 'Classificata',
      ratingsHint: 'Le tue due valutazioni: da ladro e da infiltrato.',
      placing: 'Piazzamento: {{games}} partite su {{total}}',
      games: '{{games}} partite',
      challenges: 'Sfide',
      daily: 'Oggi',
      weekly: 'Questa settimana',
      done: 'Fatta',
      reward: '+{{xp}} XP',
      trophies: 'Trofei',
      trophiesCount: '{{owned}} su {{total}}',
      recent: 'Gli ultimi colpi',
      recentEmpty:
        'Ancora nessun colpo online. Gioca una partita rapida o apri una stanza privata.',
      won: 'Vinto',
      lost: 'Perso',
      left: 'Lasciato',
      asInfiltrator: 'da Talpa',
      empty: 'Qui non c’è ancora niente: gioca un colpo online e si riempie.',
      toCollection: 'Apri la collezione',
      toLeaderboards: 'Classifiche',
      replay: 'Rivedi',
    },
    stats: {
      matches: 'Colpi giocati',
      heists: 'Colpi portati a casa',
      wins: 'Vittorie',
      loot: 'Bottino portato a casa',
      lootUnit: '{{amount}} M di lire',
      bestLoot: 'Parte migliore',
      asInfiltrator: 'Giocati da Talpa',
      infiltratorWins: 'Vinti da Talpa',
      unmasksRight: 'Talpe smascherate',
      unmasksWrong: 'Accuse sbagliate',
      abandoned: 'Colpi lasciati',
      dailyRuns: 'Colpi del giorno giocati',
    },
    leagues: {
      pickpocket: 'Borseggiatore',
      thief: 'Ladro',
      professional: 'Professionista',
      master: 'Maestro',
      legend: 'Leggenda',
    },
    ratingKinds: { thief: 'Da ladro', infiltrator: 'Da Talpa' },
    modes: {
      quick: 'Partita rapida',
      private: 'Stanza privata',
      ranked: 'Classificata',
      daily: 'Colpo del giorno',
    },
    challenge: {
      matches_one: 'Gioca {{count}} colpo',
      matches_other: 'Gioca {{count}} colpi',
      heists_one: 'Porta a casa {{count}} colpo',
      heists_other: 'Porta a casa {{count}} colpi',
      wins_one: 'Vinci {{count}} colpo',
      wins_other: 'Vinci {{count}} colpi',
      loot_one: 'Porta a casa {{count}} milione di lire',
      loot_other: 'Porta a casa {{count}} milioni di lire',
      unmasksRight_one: 'Smaschera la Talpa {{count}} volta',
      unmasksRight_other: 'Smaschera la Talpa {{count}} volte',
      infiltratorWins_one: 'Vinci {{count}} colpo da Talpa',
      infiltratorWins_other: 'Vinci {{count}} colpi da Talpa',
      cleanHeists_one: 'Porta a casa {{count}} colpo con l’allarme al 30 % o meno',
      cleanHeists_other: 'Porta a casa {{count}} colpi con l’allarme al 30 % o meno',
      dailyRuns_one: 'Gioca {{count}} Colpo del giorno',
      dailyRuns_other: 'Gioca {{count}} Colpi del giorno',
      role_one: 'Gioca {{count}} colpo come {{role}}',
      role_other: 'Gioca {{count}} colpi come {{role}}',
      map_one: 'Gioca {{count}} colpo: {{map}}',
      map_other: 'Gioca {{count}} colpi: {{map}}',
    },
    trophy: {
      first_heist: { name: 'Primo colpo', desc: 'Porta a casa il tuo primo colpo.' },
      ten_heists: { name: 'Dieci colpi', desc: 'Porta a casa 10 colpi.' },
      fifty_heists: { name: 'Cinquanta colpi', desc: 'Porta a casa 50 colpi.' },
      millionaire: { name: 'Miliardario', desc: 'Porta a casa in tutto 1000 milioni di lire.' },
      fat_share: {
        name: 'La parte del leone',
        desc: 'Porta a casa 150 milioni di lire in un colpo solo.',
      },
      mole_hunter: { name: 'Cacciatore di talpe', desc: 'Nomina la vera Talpa 5 volte.' },
      bloodhound: { name: 'Segugio', desc: 'Nomina la vera Talpa 25 volte.' },
      double_agent: { name: 'Doppiogiochista', desc: 'Vinci 3 colpi da Talpa.' },
      daily_regular: { name: 'Habitué del giorno', desc: 'Gioca 7 Colpi del giorno.' },
      veteran: { name: 'Veterano', desc: 'Gioca 100 colpi.' },
      clean_getaway: {
        name: 'Fuga pulita',
        desc: 'Porta a casa un colpo senza che l’allarme salga mai.',
      },
      legend_job: { name: 'Un colpo da leggenda', desc: 'Vinci un colpo in difficoltà Leggenda.' },
    },
    cosmetic: {
      hat_tophat: 'Cilindro',
      hat_panama: 'Panama',
      hat_hood: 'Cappuccio',
      mask_fox: 'Maschera da volpe',
      mask_skull: 'Maschera da teschio',
      coat_crimson: 'Cappotto cremisi',
      coat_ivory: 'Cappotto avorio',
      rim_gold: 'Luce dorata',
      rim_ghost: 'Luce spettrale',
      suit_crown: 'Corona',
      blueprint_sepia: 'Cianografia seppia',
      blueprint_noir: 'Cianografia noir',
      emote_cigar: 'Sigaro',
      emote_watch: 'Orologio da tasca',
      emote_glass: 'Calice alzato',
      emote_magnifier: 'Lente d’ingrandimento',
      car_coupe: 'Coupé',
      car_taxi: 'Taxi',
      car_roadster: 'Spider',
    },
    parts: {
      hat: 'Cappello',
      mask: 'Maschera',
      coat: 'Cappotto',
      rim: 'Luce',
      suit: 'Emblema',
      blueprint: 'Cianografia',
      emote: 'Emote',
      car: 'Auto della fuga',
    },
    unlock: {
      level: 'Raggiungi il livello {{level}}.',
      trophy: 'Ottieni il trofeo «{{name}}».',
      league: 'Raggiungi la lega {{league}} in Classificata.',
    },
    collection: {
      title: 'Collezione',
      intro: 'Tutto quello che c’è qui si guadagna giocando. Niente è in vendita.',
      owned: '{{owned}} su {{total}} sbloccati',
      equip: 'Indossa',
      equipped: 'Indossato',
      unequip: 'Togli',
      locked: 'Bloccato',
      unlocked: 'Sbloccata',
      free: 'Gratis',
      classic: 'Cianografia classica',
      classicCar: 'Berlina',
      toProfile: 'Cambia il tuo aspetto',
      saved: 'Salvato.',
      failed: 'Non è andata. Riprova.',
      blueprintHint: 'L’aspetto del piano che disegni e leggi prima di un colpo.',
      emoteHint:
        'Si mandano al tavolo dalla chat, in un colpo o in una stanza. Quattro sono libere fin dall’inizio.',
      carHint: 'L’auto che guidi nella fuga. La vedi solo tu.',
    },
    leaderboards: {
      title: 'Classifiche',
      thief: 'Valutazione da ladro',
      infiltrator: 'Valutazione da Talpa',
      xp: 'Esperienza',
      season: 'Stagione {{n}}',
      seasonHint: 'Compaiono solo i giocatori che hanno finito le cinque partite di piazzamento.',
      empty: 'Su questa classifica non c’è ancora nessuno.',
      position: 'Posto',
      player: 'Giocatore',
      rating: 'Valutazione',
      games: '{{games}} partite',
      level: 'Livello {{level}}',
      you: 'tu',
      youOutside: 'Non sei ancora su questa classifica.',
      tabs: 'Classifiche',
      tabThief: 'Ladro',
      tabInfiltrator: 'Talpa',
      tabXp: 'Esperienza',
    },
    daily: {
      title: 'Colpo del giorno',
      intro:
        'Un colpo al giorno, uguale per tutti: la stessa mappa e lo stesso ruolo, e tre tentativi. Conta il migliore: più bottino, poi il più veloce.',
      today: 'Il colpo di oggi',
      map: 'Mappa',
      role: 'Il tuo ruolo',
      difficulty: 'Difficoltà',
      notReady: 'Il colpo di oggi non è ancora pronto. Riprova fra un momento.',
      attempts: 'I tuoi tentativi',
      attemptLine: 'Tentativo {{n}}',
      attemptsLeft_one: 'Ti resta {{count}} tentativo oggi.',
      attemptsLeft_other: 'Ti restano {{count}} tentativi oggi.',
      attemptsNone: 'Hai usato i tre tentativi di oggi. Torna domani.',
      left: 'Lasciato prima della fine',
      failed: 'Il colpo è fallito',
      loot: '{{amount}} M di lire',
      play: 'Gioca il colpo di oggi',
      next: 'Il prossimo colpo fra {{hours}} h {{minutes}} min',
      tomorrow: 'Domani: {{map}}, come {{role}}',
      board: 'La classifica di oggi',
      boardEmpty: 'Nessuno ha ancora portato a casa il colpo.',
      boardHint: 'Gli ospiti giocano, ma non sono in classifica.',
      timeUnit: '{{seconds}} s',
      you: 'tu',
      starting: 'Apro il tavolo…',
    },
    news: {
      title: 'Novità',
      empty: 'Ancora nessuna novità.',
      off: 'La pagina delle novità è spenta per ora.',
      pinned: 'In evidenza',
    },
    settlement: {
      title: 'Cosa ti ha dato il colpo',
      xp: '+{{xp}} XP',
      level: 'Livello {{level}}',
      levelUp: 'Sali di livello! Livello {{level}}',
      breakdown: 'Da dove vengono gli XP',
      participation: 'Aver partecipato',
      success: 'Il colpo è riuscito',
      win: 'Hai vinto',
      unmask: 'Hai nominato la vera Talpa',
      infiltratorWin: 'La Talpa vince',
      firstOfDay: 'Primo colpo del giorno, raddoppiato',
      challenges: 'Sfide',
      challengeDone: 'Sfida completata: +{{xp}} XP',
      trophies: 'Nuovi trofei',
      cosmetics: 'Novità nella collezione',
      rating: 'Valutazione {{before}} → {{after}}',
      placing: 'Partite di piazzamento: {{games}} su {{total}}',
      none: 'Questo colpo non ha contato per te.',
      toRapSheet: 'Apri la fedina penale',
      replay: 'Rivedilo',
    },
    replay: {
      title: 'Replay',
      off: 'I replay sono spenti per ora.',
      tag: 'Replay',
      controls: 'Comandi del replay',
      play: 'Riproduci',
      pause: 'Pausa',
      time: 'Tempo del colpo',
      speed: 'Velocità',
      watchAs: 'Guarda come',
      bot: 'Bot ({{seat}})',
      notFound:
        'Questo replay non è disponibile. Solo chi ha partecipato a un colpo può rivederlo, e solo se il server di gioco lo ha registrato.',
      oldRules:
        'Questo colpo è stato giocato con altre regole (versione {{version}}) e questa versione del gioco non può mostrarlo.',
    },
  },
};
